import React from 'react';

import { Text } from '@reckon-web/text';
import { Stack } from '@reckon-web/stack';
import { field, useForm, useSubmit, validate } from '@reckon-web/forms';
import { Checkbox } from '@reckon-web/checkbox';

import { Dialog } from '../components/common/dialog';
import { useUpdateUserFilter } from '../data/useUpdateUserFilter';
import {
  GetUserFilterListResult,
  useGetUserFilterList,
} from '../data/useGetUserFilterList';
import { TextField } from '../components/common/text-field';

import { useDialog } from './use-dialog';

type UserFilterEditDialogResult = { name: string; isDefault?: boolean };

const UserFilterEditDialog: React.FC<{
  name: string;
  isDefault?: boolean;
  onClose: (res?: UserFilterEditDialogResult) => void;
}> = ({ name, isDefault, onClose }) => {
  const schema = field.object({
    name: field.text({
      validate: validate((value) => {
        validate.required(value, 'Filter name ');
        return value;
      }),
    }),
    isDefault: field.boolean(),
  });

  const form = useForm(schema, { name, isDefault });

  const submit = useSubmit(form, (value) => onClose(value));

  return (
    <Dialog
      title="Change filter name to..."
      cancelLabel="Cancel"
      onCancel={onClose}
      submitLabel="Save"
      onSubmit={submit}
    >
      <Stack gap="large">
        <TextField label="Filter name" {...form.fields.name.props} />
        <Checkbox
          checked={form.value.isDefault}
          onChange={(value) => {
            form.fields.isDefault.setState({
              ...form.fields.isDefault.state,
              value: value.target.checked,
            });
          }}
        >
          Set as my default filter
        </Checkbox>
      </Stack>
    </Dialog>
  );
};

const UserFilterOverwriteDialog: React.FC<{
  name: string;
  onClose: (ok?: boolean) => void;
}> = ({ name, onClose }) => {
  return (
    <Dialog
      title="Override filter?"
      cancelLabel="Cancel"
      onCancel={onClose}
      submitLabel="Yes, override"
      onSubmit={() => onClose(true)}
    >
      <Text>
        This will replace existing filter saved as{' '}
        <Text inline weight="semibold">
          {name}
        </Text>{' '}
        with current settings.
      </Text>
    </Dialog>
  );
};

export const useEditUserFilter = () => {
  const { data: userFilters } = useGetUserFilterList();
  const [updateUserFilter] = useUpdateUserFilter(); // TODO: handle loading and error states

  const [Dialog, openDialog] = useDialog();

  const editUserFilter = React.useCallback(
    async (userFilter: GetUserFilterListResult) => {
      const res = await openDialog<UserFilterEditDialogResult>(
        (props) => (
          <UserFilterEditDialog
            name={userFilter.name!}
            isDefault={!!userFilter.isDefault}
            {...props}
          />
        ),
        { size: 'small' }
      );
      if (!res) return;

      const replacesUserFilter = userFilters
        ?.filter((filter) => filter.cloudId !== userFilter.cloudId)
        .find((filter) => filter.name === res.name);

      if (
        replacesUserFilter &&
        !(await openDialog<boolean>(
          (props) => <UserFilterOverwriteDialog name={res.name} {...props} />,
          { size: 'small' }
        ))
      )
        return;

      return updateUserFilter({
        variables: {
          input: {
            cloudId: userFilter.cloudId || '',
            name: res.name,
            workView: userFilter.workView!,
            filterDefinition: userFilter.filterDefinition!,
            replacesUserFilterId: replacesUserFilter?.cloudId,
            isDefault: res.isDefault,
          },
        },
      });
    },
    [userFilters, updateUserFilter, openDialog]
  );

  return React.useMemo(
    () => [Dialog, editUserFilter] as [typeof Dialog, typeof editUserFilter],
    [Dialog, editUserFilter]
  );
};
